/**
 * Invoice print.
 *
 * Rule 46 lists what a tax invoice must carry. The print model below names each
 * required particular explicitly so a missing one is a caught defect rather
 * than a blank space on a PDF a customer has already received.
 *
 * The HTML is deliberately self-contained and print-styled — no external CSS,
 * A4 page box, and it renders identically in a browser preview and a print
 * dialogue.
 */

import { amountInWords, computeDocumentTax, rateWiseSummary, round2, type DocumentTax } from "./tax";
import { GST_STATE_CODES } from "../partner";
import type { SalesConfig, SalesDocument } from "./types";
import { DOC_LABELS } from "./types";

export interface SupplierDetails {
  legalName: string;
  tradeName?: string;
  gstin: string;
  pan?: string;
  cin?: string;
  address: string;
  stateCode: string;
  email?: string;
  phone?: string;
  logoUrl?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankIfsc?: string;
  upiId?: string;
}

export interface RecipientDetails {
  name: string;
  gstin?: string;
  billingAddress?: string;
  shippingAddress?: string;
  stateCode?: string;
  placeOfSupply: string;
}

export interface PrintModel {
  title: string;
  /** "ORIGINAL FOR RECIPIENT" and the other rule 46 copy markings. */
  copyLabel: string;
  supplier: SupplierDetails;
  recipient: RecipientDetails;
  document: SalesDocument;
  tax: DocumentTax;
  rateSummary: ReturnType<typeof rateWiseSummary>;
  amountInWords: string;
  /** Missing rule 46 particulars — blocks printing when non-empty. */
  missingParticulars: string[];
  isReverseCharge: boolean;
  showIgst: boolean;
}

export type CopyType = "original" | "duplicate" | "triplicate" | "extra";

const COPY_LABELS: Record<CopyType, string> = {
  original: "ORIGINAL FOR RECIPIENT",
  duplicate: "DUPLICATE FOR TRANSPORTER",
  triplicate: "TRIPLICATE FOR SUPPLIER",
  extra: "EXTRA COPY"
};

/**
 * Checks the rule 46 particulars. Returning the list rather than throwing lets
 * the UI show every gap at once instead of one per attempt.
 */
export const missingRule46Particulars = (
  document: SalesDocument,
  supplier: SupplierDetails,
  recipient: RecipientDetails
): string[] => {
  const missing: string[] = [];
  if (!supplier.legalName) missing.push("Supplier name");
  if (!supplier.address) missing.push("Supplier address");
  if (!supplier.gstin) missing.push("Supplier GSTIN");
  if (!document.number) missing.push("Invoice number");
  if (!document.date) missing.push("Invoice date");
  if (!recipient.name) missing.push("Recipient name");
  if (!recipient.placeOfSupply) missing.push("Place of supply");
  if (!document.lines.length) missing.push("At least one line item");
  if (document.lines.some((line) => !line.description)) missing.push("Description on every line");
  if (document.lines.some((line) => !line.hsnSac)) missing.push("HSN or SAC on every line");
  // Where the recipient is registered, both name and GSTIN are mandatory.
  if (recipient.gstin === undefined && document.supplyType !== "export") {
    // Unregistered B2C is legitimate; only flag when a GSTIN was expected.
  }
  return missing;
};

export const buildPrintModel = (
  document: SalesDocument,
  supplier: SupplierDetails,
  recipient: RecipientDetails,
  config: SalesConfig,
  copy: CopyType = "original"
): PrintModel => {
  const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
    supplyType: document.supplyType,
    rcm: document.rcm,
    roundOff: config.roundOff,
    tcsRate: document.tcsRate,
    tdsRate: document.tdsRate
  });

  return {
    title: DOC_LABELS[document.type].toUpperCase(),
    copyLabel: COPY_LABELS[copy],
    supplier,
    recipient,
    document,
    tax,
    rateSummary: rateWiseSummary(tax),
    amountInWords: amountInWords(tax.total),
    missingParticulars: missingRule46Particulars(document, supplier, recipient),
    isReverseCharge: Boolean(document.rcm),
    showIgst: tax.igst > 0 || document.supplyType !== "intra-state"
  };
};

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const inr = (value: number): string =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(round2(value));

const formatDate = (value: string): string => {
  if (!value) return "";
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${day}-${month}-${year}`;
};

/** Renders the invoice as a self-contained, print-ready A4 document. */
export const renderInvoiceHtml = (model: PrintModel): string => {
  const { document, tax, supplier, recipient } = model;
  const stateName = GST_STATE_CODES[recipient.placeOfSupply] ?? recipient.placeOfSupply;

  const lineRows = document.lines
    .map((line, index) => {
      const computed = tax.lines.find((entry) => entry.lineId === line.id);
      if (!computed) return "";
      const taxCells = model.showIgst
        ? `<td class="num">${computed.gstRate}%</td><td class="num">${inr(computed.igst)}</td>`
        : `<td class="num">${computed.gstRate / 2}%</td><td class="num">${inr(computed.cgst)}</td>
           <td class="num">${computed.gstRate / 2}%</td><td class="num">${inr(computed.sgst)}</td>`;
      return `<tr>
        <td class="num">${index + 1}</td>
        <td>${escapeHtml(line.description)}</td>
        <td class="mono">${escapeHtml(line.hsnSac ?? "—")}</td>
        <td class="num">${line.quantity}</td>
        <td class="mono">${escapeHtml(line.uqc ?? "NOS")}</td>
        <td class="num">${inr(line.unitPrice)}</td>
        <td class="num">${computed.discount + computed.documentDiscountShare > 0 ? inr(computed.discount + computed.documentDiscountShare) : "—"}</td>
        <td class="num">${inr(computed.taxableValue)}</td>
        ${taxCells}
        <td class="num strong">${inr(computed.lineTotal)}</td>
      </tr>`;
    })
    .join("");

  const taxHeaders = model.showIgst
    ? `<th colspan="2">IGST</th>`
    : `<th colspan="2">CGST</th><th colspan="2">SGST</th>`;
  const taxSubHeaders = model.showIgst
    ? `<th>Rate</th><th>Amount</th>`
    : `<th>Rate</th><th>Amount</th><th>Rate</th><th>Amount</th>`;

  const summaryRows = model.rateSummary
    .map(
      (row) => `<tr>
      <td class="num">${row.gstRate}%</td>
      <td class="num">${inr(row.taxableValue)}</td>
      ${model.showIgst ? `<td class="num">${inr(row.igst)}</td>` : `<td class="num">${inr(row.cgst)}</td><td class="num">${inr(row.sgst)}</td>`}
      <td class="num">${inr(row.cess)}</td>
      <td class="num strong">${inr(row.taxableValue + row.cgst + row.sgst + row.igst + row.cess)}</td>
    </tr>`
    )
    .join("");

  const chargeRows = (document.charges ?? [])
    .map((charge) => `<tr><td>${escapeHtml(charge.label)}</td><td class="num">${inr(charge.amount)}</td></tr>`)
    .join("");

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8" />
<title>${escapeHtml(model.title)} ${escapeHtml(document.number)}</title>
<style>
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; font-size: 10px; color: #1a1a1a; margin: 0; background: #f5f6f8; }
  .sheet { width: 190mm; min-height: 277mm; margin: 8mm auto; padding: 8mm; background: #fff; border: 1px solid #d8dce3; }
  @media print { body { background: #fff; } .sheet { margin: 0; border: 0; width: auto; } .no-print { display: none !important; } }
  .doc-head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1f4fd8; padding-bottom: 6px; }
  .doc-title { text-align: right; }
  .doc-title h1 { margin: 0; font-size: 16px; letter-spacing: 1px; color: #1f4fd8; }
  .copy-label { font-size: 8px; text-transform: uppercase; letter-spacing: .8px; color: #666; }
  .supplier-name { font-size: 14px; font-weight: 700; margin: 0 0 2px; }
  .muted { color: #555; }
  .party-grid { display: flex; gap: 8px; margin-top: 8px; }
  .party-box { flex: 1; border: 1px solid #d8dce3; padding: 6px 8px; }
  .party-box h3 { margin: 0 0 3px; font-size: 9px; text-transform: uppercase; letter-spacing: .6px; color: #1f4fd8; }
  .meta { display: flex; gap: 8px; margin-top: 8px; }
  .meta div { flex: 1; border: 1px solid #d8dce3; padding: 4px 6px; }
  .meta span { display: block; font-size: 8px; text-transform: uppercase; color: #777; }
  .meta strong { font-size: 10px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { border: 1px solid #d8dce3; padding: 3px 5px; }
  thead th { background: #eef2fb; font-size: 8.5px; text-transform: uppercase; letter-spacing: .3px; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .mono { font-family: "SF Mono", Consolas, monospace; font-size: 9px; }
  .strong { font-weight: 700; }
  .totals { display: flex; gap: 8px; margin-top: 8px; }
  .totals .left { flex: 1.4; }
  .totals .right { flex: 1; }
  .totals table { margin-top: 0; }
  .grand { background: #1f4fd8; color: #fff; }
  .grand td { border-color: #1f4fd8; font-size: 12px; font-weight: 700; }
  .words { border: 1px solid #d8dce3; padding: 5px 7px; margin-top: 8px; }
  .words span { font-size: 8px; text-transform: uppercase; color: #777; display: block; }
  .footer { display: flex; gap: 8px; margin-top: 10px; }
  .footer > div { flex: 1; border: 1px solid #d8dce3; padding: 6px 8px; }
  .sign { text-align: right; padding-top: 28px; }
  .rcm-flag { background: #fff4e5; border: 1px solid #f0b667; padding: 4px 7px; margin-top: 6px; font-weight: 600; }
  .alert { background: #fdecea; border: 1px solid #e57373; padding: 6px 8px; margin-bottom: 8px; }
  .toolbar { max-width: 190mm; margin: 8mm auto 0; text-align: right; }
  .toolbar button { font: inherit; padding: 6px 14px; border: 1px solid #1f4fd8; background: #1f4fd8; color: #fff; border-radius: 4px; cursor: pointer; }
</style></head>
<body>
<div class="toolbar no-print"><button onclick="window.print()">Print / Save as PDF</button></div>
<div class="sheet">
  ${model.missingParticulars.length ? `<div class="alert no-print"><strong>Not ready to issue.</strong> Rule 46 requires: ${model.missingParticulars.map(escapeHtml).join(", ")}.</div>` : ""}

  <div class="doc-head">
    <div>
      <p class="supplier-name">${escapeHtml(supplier.tradeName || supplier.legalName)}</p>
      <div class="muted">${escapeHtml(supplier.address).replaceAll("\n", "<br/>")}</div>
      <div class="muted">GSTIN: <span class="mono strong">${escapeHtml(supplier.gstin)}</span>${supplier.pan ? ` · PAN: <span class="mono">${escapeHtml(supplier.pan)}</span>` : ""}</div>
      ${supplier.email || supplier.phone ? `<div class="muted">${escapeHtml([supplier.email, supplier.phone].filter(Boolean).join(" · "))}</div>` : ""}
    </div>
    <div class="doc-title">
      <h1>${escapeHtml(model.title)}</h1>
      <div class="copy-label">${escapeHtml(model.copyLabel)}</div>
    </div>
  </div>

  ${model.isReverseCharge ? `<div class="rcm-flag">Tax payable on reverse charge basis by the recipient — section 9(3)/9(4).</div>` : ""}

  <div class="meta">
    <div><span>${escapeHtml(model.title)} No.</span><strong class="mono">${escapeHtml(document.number)}</strong></div>
    <div><span>Date</span><strong>${formatDate(document.date)}</strong></div>
    ${document.dueDate ? `<div><span>Due date</span><strong>${formatDate(document.dueDate)}</strong></div>` : ""}
    <div><span>Place of supply</span><strong>${escapeHtml(recipient.placeOfSupply)} · ${escapeHtml(stateName)}</strong></div>
    <div><span>Supply type</span><strong>${escapeHtml(document.supplyType.replace("-", " "))}</strong></div>
  </div>

  ${document.irn ? `<div class="meta"><div><span>IRN</span><strong class="mono">${escapeHtml(document.irn)}</strong></div><div><span>Ack. No.</span><strong class="mono">${escapeHtml(document.ackNumber ?? "")}</strong></div>${document.eWayBillNumber ? `<div><span>e-Way Bill</span><strong class="mono">${escapeHtml(document.eWayBillNumber)}</strong></div>` : ""}</div>` : ""}

  <div class="party-grid">
    <div class="party-box">
      <h3>Bill to</h3>
      <strong>${escapeHtml(recipient.name)}</strong>
      <div class="muted">${escapeHtml(recipient.billingAddress ?? "").replaceAll("\n", "<br/>")}</div>
      ${recipient.gstin ? `<div class="muted">GSTIN: <span class="mono strong">${escapeHtml(recipient.gstin)}</span></div>` : `<div class="muted">Unregistered</div>`}
    </div>
    <div class="party-box">
      <h3>Ship to</h3>
      <strong>${escapeHtml(recipient.name)}</strong>
      <div class="muted">${escapeHtml(recipient.shippingAddress ?? recipient.billingAddress ?? "").replaceAll("\n", "<br/>")}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th rowspan="2">#</th><th rowspan="2">Description</th><th rowspan="2">HSN/SAC</th>
        <th rowspan="2">Qty</th><th rowspan="2">UQC</th><th rowspan="2">Rate</th>
        <th rowspan="2">Disc.</th><th rowspan="2">Taxable value</th>
        ${taxHeaders}
        <th rowspan="2">Total</th>
      </tr>
      <tr>${taxSubHeaders}</tr>
    </thead>
    <tbody>${lineRows}</tbody>
  </table>

  <div class="totals">
    <div class="left">
      <table>
        <thead><tr><th>Rate</th><th>Taxable value</th>${model.showIgst ? "<th>IGST</th>" : "<th>CGST</th><th>SGST</th>"}<th>Cess</th><th>Total</th></tr></thead>
        <tbody>${summaryRows}</tbody>
      </table>
      ${chargeRows ? `<table><thead><tr><th>Other charges</th><th>Amount</th></tr></thead><tbody>${chargeRows}</tbody></table>` : ""}
    </div>
    <div class="right">
      <table>
        <tbody>
          <tr><td>Taxable value</td><td class="num">${inr(tax.taxableValue)}</td></tr>
          ${tax.documentDiscount ? `<tr><td>Document discount</td><td class="num">(${inr(tax.documentDiscount)})</td></tr>` : ""}
          ${model.showIgst
            ? `<tr><td>IGST</td><td class="num">${inr(tax.igst)}</td></tr>`
            : `<tr><td>CGST</td><td class="num">${inr(tax.cgst)}</td></tr><tr><td>SGST</td><td class="num">${inr(tax.sgst)}</td></tr>`}
          ${tax.cess ? `<tr><td>Cess</td><td class="num">${inr(tax.cess)}</td></tr>` : ""}
          ${tax.nonTaxableCharges ? `<tr><td>Other charges</td><td class="num">${inr(tax.nonTaxableCharges)}</td></tr>` : ""}
          ${tax.roundOff ? `<tr><td>Round off</td><td class="num">${inr(tax.roundOff)}</td></tr>` : ""}
          ${tax.tcs ? `<tr><td>TCS u/s 206C(1H)</td><td class="num">${inr(tax.tcs)}</td></tr>` : ""}
          <tr class="grand"><td>Total</td><td class="num">₹ ${inr(tax.total)}</td></tr>
          ${tax.tdsWithheld ? `<tr><td class="muted">Less: TDS to be deducted${document.tdsSection ? ` u/s ${escapeHtml(document.tdsSection)}` : ""}</td><td class="num muted">(${inr(tax.tdsWithheld)})</td></tr><tr><td class="strong">Net receivable</td><td class="num strong">${inr(tax.netReceivable)}</td></tr>` : ""}
        </tbody>
      </table>
    </div>
  </div>

  <div class="words"><span>Amount in words</span><strong>${escapeHtml(model.amountInWords)}</strong></div>

  <div class="footer">
    <div>
      <strong>Bank details</strong>
      <div class="muted">${escapeHtml(supplier.bankName ?? "—")}</div>
      <div class="muted mono">A/c ${escapeHtml(supplier.bankAccountNumber ?? "—")} · IFSC ${escapeHtml(supplier.bankIfsc ?? "—")}</div>
      ${supplier.upiId ? `<div class="muted mono">UPI ${escapeHtml(supplier.upiId)}</div>` : ""}
      ${document.termsAndConditions ? `<div style="margin-top:6px"><strong>Terms</strong><div class="muted">${escapeHtml(document.termsAndConditions).replaceAll("\n", "<br/>")}</div></div>` : ""}
      ${document.notes ? `<div style="margin-top:6px" class="muted">${escapeHtml(document.notes)}</div>` : ""}
    </div>
    <div class="sign">
      <div class="muted">For <strong>${escapeHtml(supplier.tradeName || supplier.legalName)}</strong></div>
      <div style="margin-top:26px" class="muted">Authorised signatory</div>
    </div>
  </div>
</div>
</body></html>`;
};

// ------------------------------------------------------- GL posting

export interface PostingLine {
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  narration?: string;
  partnerId?: string;
  costCentre?: string;
}

/**
 * Builds the journal for a posted sales invoice.
 *
 *   Dr Trade receivables        gross including tax and TCS
 *       Cr Revenue                    taxable value by line account
 *       Cr Output CGST/SGST/IGST      tax collected
 *       Cr TCS payable                where collected
 *       Cr/Dr Rounding                difference on round-off
 *
 * TDS the customer deducts is not posted here — it only appears when the
 * receipt arrives short, because until then it is not certain it was deducted.
 */
export const buildInvoicePosting = (
  document: SalesDocument,
  config: SalesConfig,
  accounts: {
    receivable: string;
    outputCgst: string;
    outputSgst: string;
    outputIgst: string;
    outputCess: string;
    tcsPayable: string;
    roundOff: string;
    defaultRevenue: string;
  }
): { lines: PostingLine[]; balanced: boolean } => {
  const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
    supplyType: document.supplyType,
    rcm: document.rcm,
    roundOff: config.roundOff,
    tcsRate: document.tcsRate
  });

  const lines: PostingLine[] = [];
  const isCredit = document.type === "credit-note";
  const flip = (debit: number, credit: number) => (isCredit ? { debit: credit, credit: debit } : { debit, credit });

  lines.push({
    accountCode: accounts.receivable,
    accountName: "Trade receivables",
    ...flip(tax.total, 0),
    partnerId: document.partnerId,
    narration: `${DOC_LABELS[document.type]} ${document.number}`
  });

  // Revenue by line, grouped so one account produces one posting line.
  const revenueByAccount = new Map<string, number>();
  for (const line of document.lines) {
    const computed = tax.lines.find((entry) => entry.lineId === line.id);
    if (!computed) continue;
    const account = line.accountCode ?? accounts.defaultRevenue;
    revenueByAccount.set(account, round2((revenueByAccount.get(account) ?? 0) + computed.taxableValue));
  }
  for (const [accountCode, amount] of revenueByAccount) {
    lines.push({ accountCode, accountName: "Revenue", ...flip(0, amount) });
  }

  if (tax.cgst) lines.push({ accountCode: accounts.outputCgst, accountName: "Output CGST", ...flip(0, tax.cgst) });
  if (tax.sgst) lines.push({ accountCode: accounts.outputSgst, accountName: "Output SGST", ...flip(0, tax.sgst) });
  if (tax.igst) lines.push({ accountCode: accounts.outputIgst, accountName: "Output IGST", ...flip(0, tax.igst) });
  if (tax.cess) lines.push({ accountCode: accounts.outputCess, accountName: "Output cess", ...flip(0, tax.cess) });
  if (tax.tcs) lines.push({ accountCode: accounts.tcsPayable, accountName: "TCS payable", ...flip(0, tax.tcs) });
  if (tax.nonTaxableCharges) {
    lines.push({ accountCode: accounts.defaultRevenue, accountName: "Other charges", ...flip(0, tax.nonTaxableCharges) });
  }
  if (tax.roundOff) {
    const positive = tax.roundOff > 0;
    lines.push({
      accountCode: accounts.roundOff,
      accountName: "Rounding difference",
      ...flip(positive ? 0 : Math.abs(tax.roundOff), positive ? tax.roundOff : 0)
    });
  }

  const totalDebit = round2(lines.reduce((sum, line) => sum + line.debit, 0));
  const totalCredit = round2(lines.reduce((sum, line) => sum + line.credit, 0));
  return { lines, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
};

/**
 * Receipt posting. A short receipt caused by customer TDS books the deduction
 * as an asset rather than leaving the invoice looking unpaid.
 *
 *   Dr Bank                    cash actually received
 *   Dr TDS receivable          amount the customer deducted
 *       Cr Trade receivables       invoice value settled
 */
export const buildReceiptPosting = (
  receipt: { amount: number; tdsDeducted?: number; bankAccountCode?: string; partnerId: string; number: string },
  accounts: { receivable: string; bank: string; tdsReceivable: string }
): { lines: PostingLine[]; balanced: boolean } => {
  const tds = round2(receipt.tdsDeducted ?? 0);
  const cash = round2(receipt.amount);
  const lines: PostingLine[] = [
    { accountCode: receipt.bankAccountCode ?? accounts.bank, accountName: "Bank", debit: cash, credit: 0, narration: `Receipt ${receipt.number}` }
  ];
  if (tds > 0) {
    lines.push({ accountCode: accounts.tdsReceivable, accountName: "TDS receivable", debit: tds, credit: 0, narration: "TDS deducted by customer" });
  }
  lines.push({
    accountCode: accounts.receivable,
    accountName: "Trade receivables",
    debit: 0,
    credit: round2(cash + tds),
    partnerId: receipt.partnerId
  });

  const totalDebit = round2(lines.reduce((sum, line) => sum + line.debit, 0));
  const totalCredit = round2(lines.reduce((sum, line) => sum + line.credit, 0));
  return { lines, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
};

